import { Box, Grid, Typography } from '@mui/material'
import { RpcError } from '@protobuf-ts/runtime-rpc'
import { C1Button } from 'c1ui'
import { C1CheckboxProps } from 'components/core/checkbox'
import { CheckboxGroup } from 'components/core/checkbox/group'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { APIError } from 'components/core/errors/api'
import { DocsLink } from 'components/core/links/docs'
import { Role, SearchRolesRequest } from 'pbts/c1/api/iam/v1/iam'
import { SetUserRolesRequest } from 'pbts/c1/api/user/v1/user'
import React, { SyntheticEvent, useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useGetAllRoles } from 'redux_toolkit/roles'
import { useSetUserRolesApi } from 'redux_toolkit/users'

const RoleHelpText = ({ role }: { role: Role }): React.ReactElement | null => {
    switch (role.displayName.toUpperCase()) {
        case 'BASIC USER':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_1"
                        defaultMessage="Able to view and complete assigned tasks."
                    />
                </Typography>
            )
        case 'CAMPAIGN ADMINISTRATOR':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_2"
                        defaultMessage="Able to create and manage campaigns, assign tasks to users."
                    />
                </Typography>
            )
        case 'READ-ONLY USER':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_3"
                        defaultMessage="Can view but not modify content."
                    />
                </Typography>
            )
        case 'SUPER ADMINISTRATOR':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_4"
                        defaultMessage="Able to manage applications, policies, users, campaigns, and tasks."
                    />
                </Typography>
            )
        case 'INTEGRATION ADMINISTRATOR':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_5"
                        defaultMessage="Able to add and configure connectors."
                    />
                </Typography>
            )
        case 'ACCESS REQUEST HELPDESK':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_6"
                        defaultMessage="Able to create access requests and revoke tasks for others."
                    />
                </Typography>
            )
        case 'ACCESS REQUEST ADMINISTRATOR':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_7"
                        defaultMessage="Able to manage access profiles and create access requests for others."
                    />
                </Typography>
            )
        case 'CONNECTOR ADMINISTRATOR':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_8"
                        defaultMessage="Able to create and manage connectors."
                    />
                </Typography>
            )
        case 'READ-ONLY ADMINISTRATOR':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_9"
                        defaultMessage="Able to view applications, policies, users, campaigns, and tasks."
                    />
                </Typography>
            )
        case 'APPLICATION ADMINISTRATOR':
            return (
                <Typography variant="caption">
                    <FormattedMessage
                        id="component_change_role_request_help_text_10"
                        defaultMessage="Able to manage applications that they also own."
                    />
                </Typography>
            )
        default:
            return null
    }
}

export const ChangeRoleRequestDialog: React.FC<{
    close: () => void
    currentRoles: Array<Role>
    userId: string
}> = ({ close, currentRoles, userId }) => {
    const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>([])
    const [rolesCheckboxes, setRolesCheckboxes] = useState<C1CheckboxProps[]>([])
    const { mutate, inProgress, error } = useSetUserRolesApi()
    const { data: allRoles } = useGetAllRoles(SearchRolesRequest.create({}), { evictionAfterSeconds: 60 })

    const handleFormSubmission = async (e: SyntheticEvent): Promise<void> => {
        e.preventDefault()
        mutate(SetUserRolesRequest.create({ userId, roleIds: selectedRoleIds, expandMask: { paths: ['*'] } })).then(
            close,
        )
    }

    useEffect(() => {
        if (!allRoles) {
            return
        }
        const currentRoleIds = currentRoles.map((role) => role.id)
        // filter allRoles by systemApiOnly != true
        const existingRolesCheckboxes = allRoles.flat().filter(
            (role) => role.systemApiOnly !== true,
        ).map((role: Role) => {
            return {
                id: role.id,
                label: (
                    <>
                        <Typography>{role.displayName}</Typography>
                        <RoleHelpText role={role} />
                    </>
                ),
            }
        })

        setSelectedRoleIds(currentRoleIds)
        setRolesCheckboxes(existingRolesCheckboxes)
    }, [allRoles, currentRoles])

    const rolesHelpLink = (
        <DocsLink path="/docs/product/admin/user-roles">
            <FormattedMessage
                id="component_change_role_request_roles_help_link"
                defaultMessage="Learn more about roles"
            />
        </DocsLink>
    )
    return (
        <C1Dialog open onClose={close} fullWidth>
            <form onSubmit={handleFormSubmission}>
                <C1DialogTitle>
                    <FormattedMessage id="component_change_role_request_title" defaultMessage="Change user role" />
                </C1DialogTitle>
                <C1DialogContent>
                    <Grid container direction="column">
                        <Grid item>
                            {error && (
                                <APIError
                                    message={(error as RpcError).message}
                                    code={(error as RpcError).code}
                                    title={
                                        <FormattedMessage
                                            id="components_user_details_set_roles_error"
                                            defaultMessage="Error setting user roles"
                                        />
                                    }
                                />
                            )}
                        </Grid>
                        <Grid item>
                            <Box mb={3}>
                                <Typography noWrap>
                                    <FormattedMessage
                                        id="component_change_role_request_body_1"
                                        defaultMessage="Select the roles to assign to the user. {roles_help_link}"
                                        values={{ roles_help_link: rolesHelpLink }}
                                    />
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item>
                            <CheckboxGroup
                                checkboxList={rolesCheckboxes}
                                arrayState={selectedRoleIds}
                                setArrayState={setSelectedRoleIds}
                            />
                        </Grid>
                    </Grid>
                </C1DialogContent>
                <C1DialogActions>
                    <C1Button onClick={close} variant="outlined">
                        <FormattedMessage id="component_change_role_request_cancel" defaultMessage="Cancel" />
                    </C1Button>
                    <C1Button variant="contained" color="primary" type="submit" disabled={inProgress}>
                        <FormattedMessage id="component_change_role_request_cancel" defaultMessage="Save" />
                    </C1Button>
                </C1DialogActions>
            </form>
        </C1Dialog>
    )
}
